import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Property } from '../../types';
import { api, brokerService, propertyService } from '../../services/api';
import ConfirmModal from '../../components/common/ConfirmModal';
import Modal from '../../components/common/Modal';
import PhotoSequenceEditor from '../../components/property/PhotoSequenceEditor';
import { useNotification } from '../../contexts/NotificationContext';
import { transactionColor, transactionEmoji, transactionLabel } from '../../utils/transactionType';
import {
  APPROVAL_STATUS_OPTIONS,
  CHANNEL_STATUS_OPTIONS,
  COMMERCIAL_STATUS_OPTIONS,
  SITE_STATUS_OPTIONS,
  channelBadge,
  commercialStatusBadge,
  filterChipLabel,
  sitePublicationBadge,
} from '../../utils/propertyStatus';

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AddButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.light};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`;

const FiltersContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const FiltersRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  min-width: 150px;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterInput = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ActiveFilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  align-items: center;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: 2px ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FiltersActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const BulkActionsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const BulkSelectionInfo = styled.strong`
  color: #1e3a8a;
  margin-right: auto;
`;

const BulkSelect = styled.select`
  min-width: 230px;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: white;
`;

const BulkButton = styled.button<{ $secondary?: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: ${({ $secondary }) => $secondary ? '1px solid #93c5fd' : 'none'};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ $secondary }) => $secondary ? 'white' : '#2563eb'};
  color: ${({ $secondary }) => $secondary ? '#1d4ed8' : 'white'};
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SelectionCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const FilterButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${({ variant, theme }) => variant === 'primary' ? `
    background-color: ${theme.colors.primary};
    color: ${theme.colors.text.light};
    
    &:hover {
      background-color: ${theme.colors.secondary};
    }
  ` : `
    background-color: ${theme.colors.background};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border};
    
    &:hover {
      background-color: ${theme.colors.border};
    }
  `}
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
`;

const PropertiesTable = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow-x: auto;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 36px 2fr 1fr 1fr 1fr 1fr 1fr 0.8fr 0.8fr 0.8fr 1fr 1fr 200px;
  min-width: 1700px;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.background};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 36px 2fr 1fr 1fr 1fr 1fr 1fr 0.8fr 0.8fr 0.8fr 1fr 1fr 200px;
  min-width: 1700px;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${({ variant, theme }) => variant === 'edit' ? `
    background-color: ${theme.colors.primary};
    color: ${theme.colors.text.light};
    
    &:hover {
      background-color: ${theme.colors.secondary};
    }
  ` : `
    background-color: #dc3545;
    color: white;
    
    &:hover {
      background-color: #c82333;
    }
  `}
`;

const PublicationStatusGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
`;

const PublicationBadge = styled.span<{ $tone: 'success' | 'warning' | 'danger' | 'neutral' }>`
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $tone }) => $tone === 'success' ? '#d1fae5' : $tone === 'warning' ? '#fef3c7' : $tone === 'danger' ? '#fee2e2' : '#e5e7eb'};
  color: ${({ $tone }) => $tone === 'success' ? '#065f46' : $tone === 'warning' ? '#92400e' : $tone === 'danger' ? '#991b1b' : '#374151'};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.background};
  color: ${({ active, theme }) => active ? theme.colors.text.light : theme.colors.text.primary};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  min-width: 40px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: ${({ active, theme }) => active ? theme.colors.secondary : theme.colors.border};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    transform: none;
  }
`;

const PaginationInfo = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
`;

const PaginationSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.875rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PerPageSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SaveButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.light};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.border};
    cursor: not-allowed;
  }
`;

interface Filters {
  search: string;
  /** Do campo legado, so a moderacao do formulario publico continua util aqui. */
  status: string;
  commercial_status: string;
  site_publication_status: string;
  channel_status: string;
  transaction_type: string;
  type: string;
  captured_by_id: string;
  managed_by_id: string;
  sort_by: string;
  sort_order: string;
}

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_ITEMS_PER_PAGE = 10;

const AdminProperties: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Estados principais
  const [properties, setProperties] = useState<Property[]>([]);
  const [brokers, setBrokers] = useState<any[]>([]);
  const [channelPublications, setChannelPublications] = useState<Record<string, any>>({});
  const [channelQuota, setChannelQuota] = useState<{ limit: number; active: number; reserved: number; available: number } | null>(null);
  // Sem Canal Pro configurado a coluna nao aparece: "Nao enviado" em tudo seria
  // um alarme falso para quem nem usa o portal.
  const [channelConfigured, setChannelConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get('limit') || DEFAULT_ITEMS_PER_PAGE.toString()));
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkBrokerId, setBulkBrokerId] = useState('');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Estados dos filtros - inicializar com parâmetros da URL
  const [filters, setFilters] = useState<Filters>({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || 'all',
    commercial_status: searchParams.get('commercial_status') || 'all',
    site_publication_status: searchParams.get('site_publication_status') || 'all',
    channel_status: searchParams.get('channel_status') || 'all',
    transaction_type: searchParams.get('transaction_type') || 'all',
    type: searchParams.get('type') || 'all',
    captured_by_id: searchParams.get('captured_by_id') || 'all',
    managed_by_id: searchParams.get('managed_by_id') || 'all',
    sort_by: searchParams.get('sort_by') || 'id',
    sort_order: searchParams.get('sort_order') || 'desc'
  });
  
  // Estados dos modais
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; property: Property | null }>({
    isOpen: false,
    property: null
  });
  const [coverImageModal, setCoverImageModal] = useState<{ isOpen: boolean; property: Property | null }>({
    isOpen: false,
    property: null
  });
  const [selectedCoverImage, setSelectedCoverImage] = useState<string>('');
  const [galleryOrder, setGalleryOrder] = useState<string[]>([]);
  const [initialGalleryOrder, setInitialGalleryOrder] = useState<string[]>([]);
  const [savingPhotos, setSavingPhotos] = useState(false);

  // Função para atualizar a URL com os filtros atuais
  const updateURL = useCallback(() => {
    const params = new URLSearchParams();
    
    // Adicionar filtros apenas se não forem valores padrão
    if (filters.search) params.set('search', filters.search);
    if (filters.status !== 'all') params.set('status', filters.status);
    if (filters.commercial_status !== 'all') params.set('commercial_status', filters.commercial_status);
    if (filters.site_publication_status !== 'all') params.set('site_publication_status', filters.site_publication_status);
    if (filters.channel_status !== 'all') params.set('channel_status', filters.channel_status);
    if (filters.transaction_type !== 'all') params.set('transaction_type', filters.transaction_type);
    if (filters.type !== 'all') params.set('type', filters.type);
    if (filters.captured_by_id !== 'all') params.set('captured_by_id', filters.captured_by_id);
    if (filters.managed_by_id !== 'all') params.set('managed_by_id', filters.managed_by_id);
    if (filters.sort_by !== 'created_at') params.set('sort_by', filters.sort_by);
    if (filters.sort_order !== 'desc') params.set('sort_order', filters.sort_order);
    
    // Adicionar paginação apenas se não forem valores padrão
    if (currentPage !== 1) params.set('page', currentPage.toString());
    if (itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) params.set('limit', itemsPerPage.toString());
    
    setSearchParams(params);
  }, [filters, currentPage, itemsPerPage, setSearchParams]);

  // Carregar corretores apenas uma vez
  useEffect(() => {
    loadBrokers();
    loadChannelQuota();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Atualizar URL quando filtros ou paginação mudarem
  useEffect(() => {
    updateURL();
  }, [updateURL]);

  // Recarregar propriedades quando filtros, página ou itens por página mudarem
  useEffect(() => {
    fetchProperties();
  }, [filters, currentPage, itemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBrokers = async () => {
    try {
      const brokersData = await brokerService.getActive();
      setBrokers(brokersData);
    } catch (error) {
      console.error('Erro ao carregar corretores:', error);
    }
  };

  /**
   * Publicações apenas dos imóveis que estão na tela.
   *
   * Antes eram pedidas as 500 mais recentes do inventário inteiro: quem tinha
   * mais imóveis que isso via a coluna do Canal Pro simplesmente vazia, sem
   * nenhum aviso de que o dado tinha sido truncado.
   */
  const loadChannelPublications = useCallback(async (propertyIds: string[]) => {
    if (!propertyIds.length) return;
    try {
      const params = new URLSearchParams();
      propertyIds.forEach(id => params.append('property_ids', id));
      params.append('limit', String(propertyIds.length));
      const response = await api.get(`/canal-pro/sync/publications?${params.toString()}`);
      const byProperty = (response.data.items || []).reduce((result: Record<string, any>, item: any) => {
        result[item.property_id] = item;
        return result;
      }, {});
      setChannelPublications(current => ({ ...current, ...byProperty }));
      setChannelConfigured(true);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Tenant sem Canal Pro: a coluna some em vez de mostrar "Não enviado".
        setChannelConfigured(false);
        return;
      }
      console.error('Erro ao carregar publicações do Canal Pro:', error);
    }
  }, []);

  const loadChannelQuota = useCallback(async () => {
    try {
      const overview = await api.get('/canal-pro/sync/overview');
      setChannelQuota(overview.data.quota || null);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Erro ao carregar cota do Canal Pro:', error);
      }
      setChannelQuota(null);
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      
      // Paginação
      params.append('skip', String((currentPage - 1) * itemsPerPage));
      params.append('limit', String(itemsPerPage));
      
      // Filtros
      if (filters.search.trim()) {
        // Usar endpoint de busca
        params.append('query', filters.search.trim());
      }
      
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      } else {
        params.append('show_all', 'true');
      }

      if (filters.commercial_status !== 'all') {
        params.append('commercial_status', filters.commercial_status);
      }

      if (filters.site_publication_status !== 'all') {
        params.append('site_publication_status', filters.site_publication_status);
      }

      if (filters.channel_status !== 'all') {
        params.append('channel_status', filters.channel_status);
      }
      
      if (filters.transaction_type !== 'all') {
        params.append('transaction_type', filters.transaction_type);
      }
      
      if (filters.type !== 'all') {
        params.append('type', filters.type);
      }
      
      if (filters.captured_by_id !== 'all') {
        params.append('captured_by_id', filters.captured_by_id);
      }
      
      if (filters.managed_by_id !== 'all') {
        params.append('managed_by_id', filters.managed_by_id);
      }
      
      // Adicionar ordenação
      params.append('sort_by', filters.sort_by);
      params.append('sort_order', filters.sort_order);
      
      // Escolher endpoint baseado na busca
      const endpoint = filters.search.trim() ? '/properties/search' : '/properties/';
      const url = `${endpoint}?${params.toString()}`;
      
      const response = await api.get(url);
      
      // Verificar se a resposta tem a estrutura paginada
      if (response.data && typeof response.data === 'object' && 'items' in response.data) {
        // Nova estrutura paginada
        const items = response.data.items || [];
        setProperties(items);
        setTotalCount(response.data.total || 0);
        loadChannelPublications(items.map((item: Property) => item.id));
      } else if (Array.isArray(response.data)) {
        loadChannelPublications(response.data.map((item: Property) => item.id));
        // Estrutura antiga (fallback)
        setProperties(response.data);
        setTotalCount(response.data.length < itemsPerPage ? 
          (currentPage - 1) * itemsPerPage + response.data.length :
          currentPage * itemsPerPage + 1
        );
      } else {
        setProperties([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Erro ao carregar propriedades:', error);
      showNotification('Erro ao carregar propriedades', 'error');
      setProperties([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage, showNotification]);

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Se mudou sort_by ou sort_order, não atualizar ainda se o outro também vai mudar
      // Isso evita duas requisições quando mudamos a ordenação
      if (field === 'sort_by' || field === 'sort_order') {
        return newFilters;
      }
      
      return newFilters;
    });
    setSelectedPropertyIds([]);
    setCurrentPage(1); // Reset para primeira página quando filtro muda
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset para primeira página quando itens por página muda
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      commercial_status: 'all',
      site_publication_status: 'all',
      channel_status: 'all',
      transaction_type: 'all',
      type: 'all',
      captured_by_id: 'all',
      managed_by_id: 'all',
      sort_by: 'created_at',
      sort_order: 'desc'
    });
    setSelectedPropertyIds([]);
    setCurrentPage(1);
  };

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'search') return value.trim() !== '';
      if (key === 'sort_by') return value !== 'created_at';
      if (key === 'sort_order') return value !== 'desc';
      return value !== 'all';
    });
  }, [filters]);

  /** Filtros de estado aplicados, cada um removível pelo próprio chip. */
  const activeStatusChips = useMemo(() => {
    const fields = ['commercial_status', 'site_publication_status', 'channel_status', 'status'] as const;
    return fields
      .map(field => ({ field, label: filterChipLabel(field, filters[field]) }))
      .filter((chip): chip is { field: typeof fields[number]; label: string } => Boolean(chip.label));
  }, [filters]);

  const allPageSelected = properties.length > 0 && properties.every(property => selectedPropertyIds.includes(property.id));
  const bulkNeedsBroker = bulkAction === 'captured_assign' || bulkAction === 'managed_assign';
  const canApplyBulkAction = Boolean(
    selectedPropertyIds.length && bulkAction && (!bulkNeedsBroker || bulkBrokerId)
  );

  const bulkActionLabels: Record<string, string> = {
    site_published: 'Publicar no site',
    site_hidden: 'Ocultar do site',
    site_draft: 'Mover para rascunho no site',
    commercial_available: 'Marcar como disponível',
    commercial_reserved: 'Marcar como reservado',
    commercial_sold: 'Marcar como vendido',
    commercial_rented: 'Marcar como alugado',
    commercial_withdrawn: 'Marcar como retirado',
    commercial_unknown: 'Marcar para revisão',
    price_show: 'Mostrar preço',
    price_hide: 'Ocultar preço',
    captured_assign: 'Atribuir corretor captador',
    captured_clear: 'Remover corretor captador',
    managed_assign: 'Atribuir corretor administrador',
    managed_clear: 'Remover corretor administrador',
    channel_active: 'Ativar no Canal Pro',
    channel_inactive: 'Desativar no Canal Pro',
    channel_draft: 'Mover para rascunho no Canal Pro'
  };

  const togglePropertySelection = (propertyId: string) => {
    setSelectedPropertyIds(current => current.includes(propertyId)
      ? current.filter(id => id !== propertyId)
      : [...current, propertyId]
    );
  };

  const toggleCurrentPageSelection = () => {
    const pageIds = properties.map(property => property.id);
    setSelectedPropertyIds(current => allPageSelected
      ? current.filter(id => !pageIds.includes(id))
      : Array.from(new Set([...current, ...pageIds]))
    );
  };

  const selectAllFilteredResults = async () => {
    try {
      const params = new URLSearchParams();
      params.append('skip', '0');
      params.append('limit', String(Math.min(totalCount, 500)));
      if (filters.search.trim()) params.append('query', filters.search.trim());
      if (filters.status !== 'all') params.append('status', filters.status);
      else params.append('show_all', 'true');
      if (filters.commercial_status !== 'all') params.append('commercial_status', filters.commercial_status);
      if (filters.site_publication_status !== 'all') params.append('site_publication_status', filters.site_publication_status);
      if (filters.channel_status !== 'all') params.append('channel_status', filters.channel_status);
      if (filters.transaction_type !== 'all') params.append('transaction_type', filters.transaction_type);
      if (filters.type !== 'all') params.append('type', filters.type);
      if (filters.captured_by_id !== 'all') params.append('captured_by_id', filters.captured_by_id);
      if (filters.managed_by_id !== 'all') params.append('managed_by_id', filters.managed_by_id);

      const endpoint = filters.search.trim() ? '/properties/search' : '/properties/';
      const response = await api.get(`${endpoint}?${params.toString()}`);
      const items = response.data.items || response.data || [];
      setSelectedPropertyIds(items.map((property: Property) => property.id));
      if (totalCount > 500) {
        showNotification('Foram selecionados os primeiros 500 resultados, limite por operação.', 'error');
      }
    } catch (error) {
      console.error('Erro ao selecionar resultados:', error);
      showNotification('Não foi possível selecionar todos os resultados', 'error');
    }
  };

  const executeBulkAction = async () => {
    if (!canApplyBulkAction) return;
    setBulkConfirmOpen(false);
    setBulkProcessing(true);
    try {
      if (bulkAction.startsWith('channel_')) {
        const desiredStatus = bulkAction.replace('channel_', '').toUpperCase();
        const response = await api.patch('/canal-pro/sync/properties/bulk-status', {
          property_ids: selectedPropertyIds,
          desired_status: desiredStatus
        });
        const result = response.data;
        if (result.failed_count) {
          setSelectedPropertyIds(result.failed.map((item: any) => item.property_id));
          showNotification(
            `${result.updated_count} alterados, ${result.unchanged_count} já estavam corretos e ${result.failed_count} falharam. Os que falharam continuam selecionados.`,
            'error'
          );
        } else {
          setSelectedPropertyIds([]);
          showNotification(
            `${result.updated_count} imóveis enfileirados; ${result.unchanged_count} já estavam com esse status.`,
            'success'
          );
        }
      } else {
        const payload: any = { property_ids: selectedPropertyIds };
        if (bulkAction.startsWith('site_')) payload.site_publication_status = bulkAction.replace('site_', '');
        if (bulkAction.startsWith('commercial_')) payload.commercial_status = bulkAction.replace('commercial_', '');
        if (bulkAction === 'price_show') payload.show_price = true;
        if (bulkAction === 'price_hide') payload.show_price = false;
        if (bulkAction === 'captured_assign') payload.captured_by_id = Number(bulkBrokerId);
        if (bulkAction === 'captured_clear') payload.captured_by_id = null;
        if (bulkAction === 'managed_assign') payload.managed_by_id = Number(bulkBrokerId);
        if (bulkAction === 'managed_clear') payload.managed_by_id = null;

        const response = await api.patch('/properties/bulk-update', payload);
        setSelectedPropertyIds([]);
        showNotification(`${response.data.updated_count} imóveis atualizados com sucesso.`, 'success');
      }
      // fetchProperties ja recarrega as publicacoes da pagina; a cota e global.
      await Promise.all([fetchProperties(), loadChannelQuota()]);
    } catch (error: any) {
      console.error('Erro na ação em lote:', error);
      showNotification(error.response?.data?.detail || 'Erro ao executar ação em lote', 'error');
    } finally {
      setBulkProcessing(false);
    }
  };

  const bulkConfirmationMessage = `${bulkActionLabels[bulkAction] || 'Aplicar alteração'} em ${selectedPropertyIds.length} imóvel(is)?${
    bulkAction === 'channel_active' && channelQuota
      ? ` A cota atual possui ${channelQuota.available} vaga(s) livre(s); itens excedentes serão mantidos selecionados.`
      : ''
  }`;

  // Paginação
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const showPages = 5; // Número de páginas a mostrar
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);
    
    // Ajustar se não temos páginas suficientes no final
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationButton
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PaginationButton>
      );
    }

    return (
      <PaginationContainer>
        <PerPageSelector>
          <span>Itens por página:</span>
          <PaginationSelect
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
          >
            {ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </PaginationSelect>
        </PerPageSelector>

        <PaginationControls>
          <PaginationButton
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            ‹ Anterior
          </PaginationButton>
          
          {startPage > 1 && (
            <>
              <PaginationButton onClick={() => handlePageChange(1)}>1</PaginationButton>
              {startPage > 2 && <span>...</span>}
            </>
          )}
          
          {pages}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span>...</span>}
              <PaginationButton onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </PaginationButton>
            </>
          )}

          <PaginationButton
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Próxima ›
          </PaginationButton>
        </PaginationControls>
        
        <PaginationInfo>
          Página {currentPage} de {totalPages} ({totalCount} itens)
        </PaginationInfo>
      </PaginationContainer>
    );
  };

  // Funções de ação (mantidas)
  const handleEdit = (property: Property) => {
    navigate(`/admin/properties/edit/${property.id}`);
  };

  const handleCreate = () => {
    navigate('/admin/properties/new');
  };

  const handleDelete = (property: Property) => {
    setDeleteModal({ isOpen: true, property });
  };

  const confirmDelete = async () => {
    if (!deleteModal.property) return;

    try {
      await api.delete(`/properties/${deleteModal.property.id}`);
      
      // Recarregar a página atual
      await fetchProperties();
      
      showNotification('Propriedade excluída com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao excluir propriedade:', error);
      showNotification('Erro ao excluir propriedade', 'error');
    } finally {
      setDeleteModal({ isOpen: false, property: null });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const galleryUrls = (property: Property): string[] => (
    (property.images || [])
      .map((image: any) => (typeof image === 'string' ? image : image?.url))
      .filter((url: any): url is string => typeof url === 'string' && url.length > 0)
  );

  const handleManagePhotos = (property: Property) => {
    const urls = galleryUrls(property);
    setCoverImageModal({ isOpen: true, property });
    setGalleryOrder(urls);
    setInitialGalleryOrder(urls);
    setSelectedCoverImage(property.cover_image || urls[0] || '');
  };

  const closePhotosModal = () => {
    setCoverImageModal({ isOpen: false, property: null });
    setSelectedCoverImage('');
    setGalleryOrder([]);
    setInitialGalleryOrder([]);
  };

  const orderChanged = (
    galleryOrder.length !== initialGalleryOrder.length ||
    galleryOrder.some((url, index) => url !== initialGalleryOrder[index])
  );

  const coverChanged = Boolean(
    coverImageModal.property && selectedCoverImage &&
    selectedCoverImage !== coverImageModal.property.cover_image
  );

  const photoSettingsChanged = orderChanged || coverChanged;

  const savePhotoSettings = async () => {
    const property = coverImageModal.property;
    if (!property || !photoSettingsChanged) return;

    setSavingPhotos(true);
    try {
      // Duas escritas distintas de propósito: a ordem passa pelo endpoint que
      // valida a permutação, a capa continua sendo um campo independente.
      if (orderChanged) {
        await propertyService.updateImagesOrder(property.id, galleryOrder);
      }
      if (coverChanged) {
        await api.patch(`/properties/${property.id}`, { cover_image: selectedCoverImage });
      }

      await fetchProperties();
      showNotification('Fotos atualizadas com sucesso!', 'success');
      closePhotosModal();
    } catch (error: any) {
      console.error('Erro ao salvar fotos:', error);
      showNotification(
        error?.response?.data?.detail || 'Erro ao salvar as fotos do imóvel',
        'error'
      );
    } finally {
      setSavingPhotos(false);
    }
  };


  return (
    <Container>
      <Header>
        <Title>Gerenciar Propriedades</Title>
        <AddButton onClick={handleCreate}>
          + Nova Propriedade
        </AddButton>
      </Header>

      <FiltersContainer>
        <FiltersRow>
          <FilterGroup>
            <FilterLabel>Buscar:</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Título, descrição ou localização..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Transação:</FilterLabel>
            <FilterSelect
              value={filters.transaction_type}
              onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
            >
              <option value="all">Todas as finalidades</option>
              <option value="venda">Venda</option>
              <option value="aluguel">Aluguel</option>
              <option value="venda_aluguel">Venda e Aluguel</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo:</FilterLabel>
            <FilterSelect
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="all">Todos os Tipos</option>
              <option value="apartamento">Apartamento</option>
              <option value="casa">Casa</option>
              <option value="sobrado">Sobrado</option>
              <option value="cobertura">Cobertura</option>
              <option value="terreno">Terreno</option>
              <option value="comercial">Comercial</option>
              <option value="chacara">Chácara</option>
              <option value="sitio">Sítio</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Ordenar por:</FilterLabel>
            <FilterSelect
              value={`${filters.sort_by}_${filters.sort_order}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('_');
                handleFilterChange('sort_by', sortBy);
                handleFilterChange('sort_order', sortOrder);
              }}
            >
              <option value="id_desc">ID (Mais Recentes)</option>
              <option value="id_asc">ID (Mais Antigos)</option>
              <option value="created_at_desc">Data de Criação (Recentes)</option>
              <option value="created_at_asc">Data de Criação (Antigas)</option>
              <option value="updated_at_desc">Última Atualização</option>
              <option value="price_desc">Maior Preço</option>
              <option value="price_asc">Menor Preço</option>
              <option value="title_asc">Título (A-Z)</option>
              <option value="title_desc">Título (Z-A)</option>
              <option value="area_desc">Maior Área</option>
              <option value="area_asc">Menor Área</option>
            </FilterSelect>
          </FilterGroup>
        </FiltersRow>

        <FiltersRow>
          <FilterGroup>
            <FilterLabel>Situação do imóvel:</FilterLabel>
            <FilterSelect
              value={filters.commercial_status}
              onChange={(e) => handleFilterChange('commercial_status', e.target.value)}
            >
              {COMMERCIAL_STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>No site:</FilterLabel>
            <FilterSelect
              value={filters.site_publication_status}
              onChange={(e) => handleFilterChange('site_publication_status', e.target.value)}
            >
              {SITE_STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>No Canal Pro:</FilterLabel>
            <FilterSelect
              value={filters.channel_status}
              onChange={(e) => handleFilterChange('channel_status', e.target.value)}
            >
              {CHANNEL_STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Aprovação:</FilterLabel>
            <FilterSelect
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              {APPROVAL_STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Captado por:</FilterLabel>
            <FilterSelect
              value={filters.captured_by_id}
              onChange={(e) => handleFilterChange('captured_by_id', e.target.value)}
            >
              <option value="all">Todos os Corretores</option>
              <option value="none">Sem Corretor</option>
              {brokers.map(broker => (
                <option key={broker.id} value={broker.id}>{broker.name}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Administrado por:</FilterLabel>
            <FilterSelect
              value={filters.managed_by_id}
              onChange={(e) => handleFilterChange('managed_by_id', e.target.value)}
            >
              <option value="all">Todos os Corretores</option>
              <option value="none">Sem Corretor</option>
              {brokers.map(broker => (
                <option key={broker.id} value={broker.id}>{broker.name}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

        </FiltersRow>

        {(activeStatusChips.length > 0 || hasActiveFilters) && (
          <FiltersRow>
            <ActiveFilterChips>
              {/* Cada filtro aplicado sai sozinho: antes so existia o
                  "Limpar Filtros" tudo-ou-nada, e era facil ficar com um
                  filtro esquecido explicando um resultado vazio. */}
              {activeStatusChips.map(chip => (
                <FilterChip
                  key={chip.field}
                  type="button"
                  onClick={() => handleFilterChange(chip.field, 'all')}
                  aria-label={`Remover filtro ${chip.label}`}
                >
                  {chip.label} <span aria-hidden="true">×</span>
                </FilterChip>
              ))}
            </ActiveFilterChips>
            <FiltersActions>
              {hasActiveFilters && (
                <FilterButton variant="secondary" onClick={clearFilters}>
                  Limpar Filtros
                </FilterButton>
              )}
            </FiltersActions>
          </FiltersRow>
        )}
      </FiltersContainer>

      <BulkActionsBar>
        <BulkSelectionInfo>
          {selectedPropertyIds.length
            ? `${selectedPropertyIds.length} imóvel(is) selecionado(s)`
            : 'Selecione imóveis para executar ações em lote'}
        </BulkSelectionInfo>
        <BulkButton $secondary onClick={toggleCurrentPageSelection} disabled={!properties.length || bulkProcessing}>
          {allPageSelected ? 'Desmarcar página' : 'Selecionar página'}
        </BulkButton>
        {totalCount > properties.length && (
          <BulkButton $secondary onClick={selectAllFilteredResults} disabled={bulkProcessing}>
            {totalCount > 500
              ? 'Selecionar os primeiros 500 resultados'
              : `Selecionar todos os ${totalCount} resultados`}
          </BulkButton>
        )}
        {selectedPropertyIds.length > 0 && (
          <BulkButton $secondary onClick={() => setSelectedPropertyIds([])} disabled={bulkProcessing}>
            Limpar seleção
          </BulkButton>
        )}
        <BulkSelect
          value={bulkAction}
          onChange={(e) => {
            setBulkAction(e.target.value);
            setBulkBrokerId('');
          }}
          disabled={!selectedPropertyIds.length || bulkProcessing}
          aria-label="Ação em lote"
        >
          <option value="">Escolha uma ação em lote</option>
          <optgroup label="Publicação no site">
            <option value="site_published">Publicar no site</option>
            <option value="site_hidden">Ocultar do site</option>
            <option value="site_draft">Mover para rascunho</option>
          </optgroup>
          <optgroup label="Situação comercial">
            <option value="commercial_available">Marcar como disponível</option>
            <option value="commercial_reserved">Marcar como reservado</option>
            <option value="commercial_sold">Marcar como vendido</option>
            <option value="commercial_rented">Marcar como alugado</option>
            <option value="commercial_withdrawn">Marcar como retirado</option>
            <option value="commercial_unknown">Marcar para revisão</option>
          </optgroup>
          <optgroup label="Preço no site e canais">
            <option value="price_show">Mostrar preço</option>
            <option value="price_hide">Ocultar preço</option>
          </optgroup>
          <optgroup label="Corretores">
            <option value="captured_assign">Atribuir captador</option>
            <option value="captured_clear">Remover captador</option>
            <option value="managed_assign">Atribuir administrador</option>
            <option value="managed_clear">Remover administrador</option>
          </optgroup>
          {/* A cota aparece no proprio rotulo: decidir antes de aplicar evita
              selecionar 20 imoveis para 3 vagas livres. */}
          <optgroup label="Canal Pro">
            <option value="channel_active">
              {channelQuota
                ? `Ativar no Canal Pro (${channelQuota.available} vaga(s) livre(s))`
                : 'Ativar no Canal Pro'}
            </option>
            <option value="channel_inactive">Desativar no Canal Pro</option>
            <option value="channel_draft">Mover para rascunho no Canal Pro</option>
          </optgroup>
        </BulkSelect>
        {bulkNeedsBroker && (
          <BulkSelect
            value={bulkBrokerId}
            onChange={(e) => setBulkBrokerId(e.target.value)}
            disabled={bulkProcessing}
            aria-label="Corretor para ação em lote"
          >
            <option value="">Escolha o corretor</option>
            {brokers.map(broker => (
              <option key={broker.id} value={broker.id}>{broker.name} — {broker.creci}</option>
            ))}
          </BulkSelect>
        )}
        <BulkButton
          onClick={() => setBulkConfirmOpen(true)}
          disabled={!canApplyBulkAction || bulkProcessing}
        >
          {bulkProcessing ? 'Aplicando...' : 'Aplicar'}
        </BulkButton>
      </BulkActionsBar>

      <ResultsInfo>
        <span>
          {loading ? 'Carregando...' : `${properties.length} propriedades encontradas`}
          {hasActiveFilters && ' (filtrado)'}
        </span>
        <span>
          Página {currentPage} de {Math.max(1, totalPages)}
        </span>
      </ResultsInfo>

      {loading ? (
        <LoadingMessage>Carregando propriedades...</LoadingMessage>
      ) : (
        <>
          <PropertiesTable>
            <TableHeader>
              <div>
                <SelectionCheckbox
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={toggleCurrentPageSelection}
                  aria-label="Selecionar todos os imóveis desta página"
                />
              </div>
              <div>Título</div>
              <div>Tipo</div>
              <div>Transação</div>
              <div>Preço</div>
              <div>Localização</div>
              <div>Publicações</div>
              <div>Quartos</div>
              <div>Banheiros</div>
              <div>Suítes</div>
              <div>Captado por</div>
              <div>Administrado por</div>
              <div>Ações</div>
            </TableHeader>

            {properties.length > 0 ? (
              properties.map(property => (
                <TableRow key={property.id}>
                  <TableCell>
                    <SelectionCheckbox
                      type="checkbox"
                      checked={selectedPropertyIds.includes(property.id)}
                      onChange={() => togglePropertySelection(property.id)}
                      aria-label={`Selecionar ${property.title}`}
                    />
                  </TableCell>
                  <TableCell>{property.title}</TableCell>
                  <TableCell>{property.type}</TableCell>
                  <TableCell>
                    {property.transaction_type ? (
                      <span style={{
                        color: transactionColor(property.transaction_type),
                        fontWeight: 'bold'
                      }}>
                        {transactionEmoji(property.transaction_type)} {transactionLabel(property.transaction_type)}
                      </span>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </TableCell>
                  <TableCell>{formatPrice(property.price)}</TableCell>
                  <TableCell>{property.location}</TableCell>
                  <TableCell>
                    <PublicationStatusGroup>
                      <PublicationBadge $tone={commercialStatusBadge(property).tone}>
                        Imóvel: {commercialStatusBadge(property).label}
                      </PublicationBadge>
                      <PublicationBadge $tone={sitePublicationBadge(property).tone}>
                        Site: {sitePublicationBadge(property).label}
                      </PublicationBadge>
                      {channelConfigured && (
                        <PublicationBadge
                          $tone={channelBadge(channelPublications[property.id]).tone}
                          title={channelBadge(channelPublications[property.id]).detail}
                        >
                          Canal Pro: {channelBadge(channelPublications[property.id]).label}
                        </PublicationBadge>
                      )}
                    </PublicationStatusGroup>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontSize: '0.9rem', textAlign: 'center' }}>
                      {property.bedrooms ? `${property.bedrooms}` : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontSize: '0.9rem', textAlign: 'center' }}>
                      {property.bathrooms ? `${property.bathrooms}` : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontSize: '0.9rem', textAlign: 'center' }}>
                      {property.suites ? `${property.suites}` : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>
                      {(property as any).captured_by?.name || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>
                      {(property as any).managed_by?.name || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <ActionButtons>
                      <ActionButton 
                        style={{ backgroundColor: '#10b981', color: 'white', fontSize: '0.75rem', padding: '4px 6px' }}
                        onClick={() => window.open(`/imovel/${property.id}`, '_blank')}
                        title="Visualizar página pública"
                      >
                        👁️
                      </ActionButton>
                      <ActionButton 
                        variant="edit" 
                        style={{ fontSize: '0.75rem', padding: '4px 6px' }}
                        onClick={() => handleEdit(property)}
                        title="Editar propriedade"
                      >
                        ✏️
                      </ActionButton>
                      <ActionButton 
                        style={{ backgroundColor: '#8b5cf6', color: 'white', fontSize: '0.75rem', padding: '4px 6px' }}
                        onClick={() => handleManagePhotos(property)}
                        title="Ordenar fotos e definir a capa"
                      >
                        📸
                      </ActionButton>
                      <ActionButton 
                        variant="delete" 
                        style={{ fontSize: '0.75rem', padding: '4px 6px' }}
                        onClick={() => handleDelete(property)}
                        title="Excluir propriedade"
                      >
                        🗑️
                      </ActionButton>
                    </ActionButtons>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <EmptyMessage>
                {hasActiveFilters ? 
                  'Nenhuma propriedade encontrada com os filtros aplicados.' :
                  'Nenhuma propriedade cadastrada.'
                }
              </EmptyMessage>
            )}
          </PropertiesTable>

          {renderPagination()}
        </>
      )}

      <ConfirmModal
        isOpen={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        onConfirm={executeBulkAction}
        title="Confirmar ação em lote"
        message={bulkConfirmationMessage}
        confirmText="Aplicar alteração"
        cancelText="Cancelar"
        type={bulkAction === 'channel_active' || bulkAction.includes('sold') || bulkAction.includes('withdrawn') ? 'warning' : 'info'}
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, property: null })}
        onConfirm={confirmDelete}
        title="Excluir Propriedade"
        message={`Tem certeza que deseja excluir a propriedade "${deleteModal.property?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      <Modal
        isOpen={coverImageModal.isOpen}
        onClose={closePhotosModal}
        title={`Fotos do Imóvel - ${coverImageModal.property?.title}`}
        size="lg"
      >
        <div>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Arraste pelo número (⠿) ou use as setas para mudar a sequência.
            {' '}Clique em "Definir capa" para escolher a imagem principal.
          </p>

          {galleryOrder.length > 0 ? (
            <>
              <PhotoSequenceEditor
                images={galleryOrder}
                coverImage={selectedCoverImage}
                onChange={setGalleryOrder}
                onSetCover={setSelectedCoverImage}
              />

              <SaveButton
                onClick={savePhotoSettings}
                disabled={savingPhotos || !photoSettingsChanged}
              >
                {savingPhotos ? 'Salvando...' : 'Salvar fotos'}
              </SaveButton>
            </>
          ) : (
            <p style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>
              Esta propriedade não possui imagens cadastradas.
            </p>
          )}
        </div>
      </Modal>
    </Container>
  );
};

export default AdminProperties; 
